//TODO: Make this customisable

import { Extent, getArea } from "ol/extent"
import { createXYZ } from "ol/tilegrid"
import * as proj4 from "proj4"
import { BooleanTileGrid, TileGridJSON, fromJSON } from "./tile_grid"
import { GeoJSON } from "ol/format"
import { Polygon } from "ol/geom"
import { ProjectProperties } from "./components"

const westHorsely = [-49469.089243, 6669018.450996]
const bexhill = [55641.379277, 6570068.329224]

const crawley = [-20839.008676500813, 6640614.986501137]
const seaford = [12889.487811, 6579722.087031]

export const EPSG4326 = 'EPSG:4326'
export const EPSG3857 = 'EPSG:3857'

// Crawley: -20839.008676500813, 6640614.986501137
// Seaford: 12889.487811, 6579722.087031

// original extent = GREATER WAKEHURST
export const oldExtent = [-20839.008676500813, 6579722.087031, 12889.487811, 6640614.986501137]

// default extent = west horsely to english channel, inline with bexhill - CENTRAL SUSSEX
export const currentExtent = [-49469.089243, 6570068.329224, 55641.379277, 6669018.450996]

//legacy static bbox
export const currentBbox = `${currentExtent.join(",")},EPSG:3857`

//legacy static zoom level
export const zoomLevel = 20

// WIP: Sets global zoom level from extent, requires tweaking and tests
export function zoomFromExtent(extent: Extent, maxtiles: number): number {
    const tileGrid = createXYZ()
    const zoomLevels = Array.from({ length: 30 }, (_, index) => index + 1).reverse()
    
    for (const zoom of zoomLevels){
        const tiles = tileGrid.getTileRangeForExtentAndZ(extent, zoom)
        const tileCount = tiles.getWidth() * tiles.getHeight()
        if(tileCount <= maxtiles){
            return zoom
        }
    }

    return 0

}

export function WKTfromExtent(extent: Extent): string {
    
    const transform = (proj4 as any).default(EPSG3857, EPSG4326)
    
    const extent4326 = [
        transform.forward([extent[0], extent[1]])[0],
        transform.forward([extent[0], extent[1]])[1],
        transform.forward([extent[2], extent[3]])[0],
        transform.forward([extent[2], extent[3]])[1],
    ]

    return `POLYGON((
        ${extent4326[0]} ${extent4326[1]},
        ${extent4326[0]} ${extent4326[3]},
        ${extent4326[2]} ${extent4326[3]},
        ${extent4326[2]} ${extent4326[1]},
        ${extent4326[0]} ${extent4326[1]}
    ))`
}

// Required format for some requests
export function bboxFromExtent(extent: Extent): string {
    return `${extent.join(",")},EPSG:3857`
}

const maskMap = new Map<string, BooleanTileGrid>()

function maskMemoryKey(name: string, extent: Extent, zoom: number): string {
    return `${name}|${zoom}|${extent.map(coord => Math.round(coord)).join(",")}`
}

function maskMatchesRequest(mask: BooleanTileGrid, extent: Extent, zoom: number): boolean {
    if (mask.zoom !== zoom) return false
    const range = createXYZ().getTileRangeForExtentAndZ(extent, zoom)
    return mask.x === range.minX
        && mask.y === range.minY
        && mask.width === range.getWidth()
        && mask.height === range.getHeight()
}

export async function maskFromExtentAndShape(projectProps: ProjectProperties): Promise<BooleanTileGrid> {

    const { extent, zoom, mask, maskLayer, maskCQL, maskShape, externalLink, setExtraInfo } = projectProps

    if(maskShape && maskShape !== "" && mask) {
        // Create a unique ID for this shape
        const shapeHash = hashString(maskShape.toString())
        const id = `shape_${shapeHash}`
        const memoryId = maskMemoryKey(id, extent, zoom)

        if(maskMap.has(memoryId)) return maskMap.get(memoryId) as BooleanTileGrid
        
        // If shape is stored directly on the source, use that
        const shape = JSON.parse(maskShape)
        const geojson = new GeoJSON().readFeature(shape)
        const polygon = geojson.getGeometry() as Polygon

        const tileGrid = createXYZ()
        const outputTileRange = tileGrid.getTileRangeForExtentAndZ(extent, zoom)

        let mask = new BooleanTileGrid(
            zoom,
            outputTileRange.minX,
            outputTileRange.minY,
            outputTileRange.getWidth(),
            outputTileRange.getHeight()
        )

        mask.iterate(function(x, y, value){
            const center = tileGrid.getTileCoordCenter([zoom, x, y])
            if(polygon.intersectsCoordinate(center)){
                mask.set(x, y, true)
            }
        })
        
        maskMap.set(memoryId, mask)

        return mask
    }

    // One stored mask per boundary. Only reuse it when it was built for this
    // extent and zoom; a province mask must not clip a village grid.
    const id = `${maskLayer}${maskCQL}`
    const memoryId = maskMemoryKey(id, extent, zoom)
    if(maskMap.has(memoryId)) return maskMap.get(memoryId) as BooleanTileGrid
    else{
        const cachedMask : BooleanTileGrid | null = !mask ? null : await loadMask(id)
        if(cachedMask !== null && maskMatchesRequest(cachedMask, extent, zoom)) {
            maskMap.set(memoryId, cachedMask)
            return cachedMask
        }else{

            const tileGrid = createXYZ()
            const outputTileRange = tileGrid.getTileRangeForExtentAndZ(extent, zoom)

            let m = new BooleanTileGrid(
                zoom,
                outputTileRange.minX,
                outputTileRange.minY,
                outputTileRange.getWidth(),
                outputTileRange.getHeight(),
                !mask
            )

            if(!mask) return m
            else{

                setExtraInfo("Building mask: 0%. This may take a while... only required once then mask is cached.")

                const link = externalLink ? externalLink : `https://landscapes.wearepal.ai/geoserver/wfs?` 
                const response = await fetch(
                    link +
                    new URLSearchParams(
                        {
                            outputFormat: 'application/json',
                            request: 'GetFeature',
                            typeName: maskLayer,
                            srsName: 'EPSG:3857',
                            CQL_FILTER: maskCQL
                        }
                    )
                )
                
                const features = new GeoJSON().readFeatures(await response.json())

                const len = m.width * m.height
                const seg = Math.ceil(len / 100)


                for (let feature of features) {
                    const geom = feature.getGeometry()
                    if (geom === undefined || !geom) { continue }
            
                    const featureTileRange = tileGrid.getTileRangeForExtentAndZ(
                        geom.getExtent(),
                        zoom
                    )

                    let i = 0
                    
                    // Convert to async processing to allow UI updates
                    const processTile = async (x: number, y: number): Promise<void> => {
                        const center = tileGrid.getTileCoordCenter([zoom, x, y])
                        if (geom.intersectsCoordinate(center)) {
                            m.set(x, y, true)
                        }
                        i++
                        
                        // Update progress after incrementing i, but throttle updates
                        if (i % seg === 0) {
                            setExtraInfo(`Building mask: ${Math.floor((i / len) * 100)}%`)
                            // Yield control to allow React to update
                            await new Promise(resolve => setTimeout(resolve, 0))
                        }
                    }
                    
                    for (
                    let x = Math.max(featureTileRange.minX, outputTileRange.minX);
                    x <= Math.min(featureTileRange.maxX, outputTileRange.maxX);
                    ++x
                    ) {
                    for (
                        let y = Math.max(featureTileRange.minY, outputTileRange.minY);
                        y <= Math.min(featureTileRange.maxY, outputTileRange.maxY);
                        ++y
                    ) {
                        await processTile(x, y)
                    }
                    }
                }

                setExtraInfo("Mask built, saving to server...")

                maskMap.set(memoryId, m)
                if (cachedMask === null) saveMask(m, id)
                setExtraInfo(null)

                return m
            }
        }
    }
}

function saveMask(mask: BooleanTileGrid, id: string){
    id = id.replace(/'/g, "_")
    const json = mask.toJSON()
    const formData = new FormData()
    const blob = new Blob([JSON.stringify(json)], { type: "application/json" })
    formData.append('file', blob, 'mask.json')
    formData.append('name', id)
    const request = new XMLHttpRequest()
    request.open('POST', `/masks`)
    request.setRequestHeader('X-CSRF-Token', (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content)
    request.send(formData)
}

function loadMask(id: string): Promise<BooleanTileGrid | null> {
    id = id.replace(/'/g, "_")
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        request.open('GET', `/masks?name=${id}`)
        
        const csrfTokenElement = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
        if (csrfTokenElement) {
            request.setRequestHeader('X-CSRF-Token', csrfTokenElement.content)
        }

        request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
                if (request.status === 200) {
                    try {
                        const response = JSON.parse(request.responseText)
                        resolve(fromJSON(response as TileGridJSON) as BooleanTileGrid)
                    } catch (error) {
                        reject(error)
                    }
                } else {
                    resolve(null)
                }
            }
        };

        request.send()
    });
}

// Helper function to create a simple hash from a string
function hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
}
